import React, { memo, useCallback, use, useEffect, useMemo, useState } from "react";
import type { BaseSelectRef } from "rc-select";
import { DefaultOptionType } from "rc-select/lib/Select";
import { Select } from "antd";
import { CompanyContext } from "../../../../../../scripts/context/CompanyContext";
import { ProductAccessUtils } from "../../../../../../scripts/models/utils/ProductAccessUtils";
import { KontoContext } from "../../../../../../scripts/context/accountingData/KontoEntitiesProvider";

type TProps = {
    placeholder?: React.ReactNode;
    onChange?: (v: string) => void;
    value?: string;
    disabled?: boolean;
    year: number;
};

export const TagSelect = memo(
    ({
        ref,
        placeholder,
        onChange,
        value,
        disabled,
        year,
        ...restProps
    }: TProps & {
        ref?: React.RefObject<BaseSelectRef>;
    }) => {
        const { companyGQL } = use(CompanyContext);
        const { tagsAllMap } = use(KontoContext);
        const hasAccounting = ProductAccessUtils.hasCompanyAccounting(companyGQL);
        const [placeholderText, setPlaceholderText] = useState(placeholder);
        const [initialYear] = useState(year);

        const options = useMemo(() => {
            return (tagsAllMap.get(String(year)) || []).map(item => {
                return { label: `${item.num} - ${item.name}`, value: item.key };
            });
        }, [tagsAllMap, year]);

        const handleChange = useCallback(
            (changedValue: string, option: DefaultOptionType | DefaultOptionType[]) => {
                onChange?.(changedValue);
            },
            [onChange]
        );

        const filterOption = useCallback((inputValue: string, option: DefaultOptionType) => {
            if (!option) {
                return false;
            }

            return String(option.label).toLowerCase().includes(inputValue.toLowerCase());
        }, []);

        useEffect(() => {
            if (placeholder === "Diverse" && !disabled && onChange) {
                onChange(undefined);
                setPlaceholderText(undefined);
            }
        }, [disabled, placeholder]);

        useEffect(() => {
            if (initialYear !== year) {
                onChange(undefined);
            }
        }, [year]);

        return (
            <Select<string>
                ref={ref}
                {...restProps}
                disabled={disabled || !hasAccounting || !year}
                placeholder={placeholderText}
                options={options}
                value={value}
                onChange={handleChange}
                // onSearch={handleSearch}
                // onClear={handleClear}
                // onKeyDown={handleKeyDown}
                filterOption={filterOption}
                allowClear={true}
                showSearch
            />
        );
    }
);
